import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import EditableNote from "@/components/EditableNote";
import { PrioBadge } from "@/components/PrioridadToggle";
import { toast } from "sonner";
import { ESTADOS, estadoInfo, Fotos, SubirFoto } from "@/components/bases/BasesComun";

const EstadoSelect = ({ r, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const cambiar = async (estado) => {
    let cantidad;
    if (estado === "parcial") {
      const v = window.prompt(`¿Cuántas bases se terminaron ahora? (pendientes: ${r.pendiente})`);
      if (v === null) return;
      cantidad = parseInt(v, 10);
    }
    if (estado === "terminado" && !window.confirm(`Marcar ${r.pendiente} base(s) como terminadas. Se descontará la madera disponible (si no hay madera, se marca igual sin descontar). ¿Continuar?`)) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/bases/solicitudes/${r.id}/estado`, { estado, cantidad });
      if (data?.advertencia) toast.warning(data.advertencia, { duration: 8000 }); else toast.success("Estado actualizado");
      onChanged();
    }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  if (r.estado === "terminado") return <span className={`text-xs font-semibold rounded px-2 py-1 ${estadoInfo(r.estado).c}`}>Terminado</span>;
  return (
    <select value={r.estado} disabled={busy} onChange={(e) => cambiar(e.target.value)}
      className={`text-xs font-semibold rounded border border-zinc-300 px-2 py-1 ${estadoInfo(r.estado).c}`} data-testid={`bases-estado-${r.numero}`}>
      {ESTADOS.map((e) => <option key={e.v} value={e.v}>{e.l}</option>)}
    </select>
  );
};

const Obs = ({ r, onSaved }) => {
  const [v, setV] = useState(r.observaciones);
  const save = async () => {
    if (v === r.observaciones) return;
    try { await api.patch(`/bases/solicitudes/${r.id}`, { observaciones: v }); onSaved(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return <textarea value={v} onChange={(e) => setV(e.target.value)} onBlur={save} rows={2} placeholder="Observaciones…"
    className="w-full min-w-[150px] text-[11px] rounded border border-zinc-200 p-1" data-testid={`bases-obs-${r.numero}`} />;
};

export const BasesTabla = ({ rows, onChanged, editable = true, extraCols = [] }) => (
  <div className="overflow-x-auto border border-zinc-200 rounded-md bg-white">
    <table className="w-full text-sm">
      <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500">
        <tr>{["Solicitud", "Pedido / Cliente", "Producto", "Tamaño y medidas", "Cant.", "Hechas", "Pend.", ...extraCols.map((c) => c.l), "Notas y observaciones", "Fotos", "Estado"].map((h) => <th key={h} className="px-3 py-2 text-left">{h}</th>)}</tr>
      </thead>
      <tbody className="divide-y divide-zinc-100">
        {rows.length === 0 && <tr><td colSpan={10 + extraCols.length} className="text-center text-zinc-400 py-10">Sin registros.</td></tr>}
        {rows.map((r, k) => (
          <tr key={`${r.id}-${k}`} className={`align-top ${r.prioridad ? "bg-red-100 hover:bg-red-200" : "hover:bg-zinc-50"}`} data-testid={`bases-row-${r.numero}`}>
            <td className="px-3 py-2 font-mono text-xs font-semibold whitespace-nowrap">{r.numero}<div className="text-[10px] font-sans font-normal text-zinc-500">{r.origen === "auto" ? "Automática" : r.origen === "fabricacion" ? "Desde Fabricación" : "Manual"}</div><PrioBadge show={r.prioridad} /></td>
            <td className="px-3 py-2 text-xs"><div className="font-mono font-semibold">{r.pedido || "—"}</div>{r.cliente}{r.fecha_entrega && <div className="text-[10px] text-orange-700">Entrega: {r.fecha_entrega}</div>}</td>
            <td className="px-3 py-2 text-xs min-w-[220px]">
              <div className="font-semibold uppercase tracking-tight leading-snug" data-testid={`bases-detalle-${r.numero}`}>{r.detalle || r.producto}</div>
              {r.modificadores && <div className="text-[10px] text-zinc-600">Extras: {r.modificadores}</div>}
              {r.unidad && <div className="text-[10px] font-normal text-amber-800" data-testid={`bases-unidad-${r.numero}`}>Unidad {r.unidad}</div>}
              {r.otros_productos?.length > 0 && <div className="mt-1 text-[10px] text-zinc-500" data-testid={`bases-otros-${r.numero}`}><span className="uppercase tracking-widest">Otros del pedido:</span>{r.otros_productos.map((p, i) => <div key={i}>{p}</div>)}</div>}
            </td>
            <td className="px-3 py-2 text-xs">{r.tamano}{r.medidas && r.medidas !== r.tamano && <div className="text-zinc-500">{r.medidas}</div>}</td>
            <td className="px-3 py-2 text-xs font-bold">{r.cantidad}</td>
            <td className="px-3 py-2 text-xs">{r.fabricada}</td>
            <td className="px-3 py-2 text-xs font-bold text-orange-700">{r.pendiente}</td>
            {extraCols.map((c) => <td key={c.l} className="px-3 py-2 text-xs whitespace-nowrap">{r[c.k]}</td>)}
            <td className="px-3 py-2 min-w-[240px] space-y-1">
              {r.notas_pedido && <div className="text-[11px] bg-zinc-100 rounded px-2 py-1 whitespace-pre-line" data-testid={`bases-nota-pedido-${r.numero}`}><b>Pedido:</b> {r.notas_pedido}</div>}
              {editable ? <Obs r={r} onSaved={onChanged} /> : r.observaciones && <div className="text-[11px]">{r.observaciones}</div>}
              {r.order_id && <EditableNote orderId={r.order_id} modulo="notas_bas" initial={r.notas_bas} onSaved={onChanged} />}
            </td>
            <td className="px-3 py-2"><Fotos fotos={r.fotos} id={r.numero} />{editable && <SubirFoto sid={r.id} onDone={onChanged} />}</td>
            <td className="px-3 py-2">{editable ? <EstadoSelect r={r} onChanged={onChanged} /> : <span className={`text-xs font-semibold rounded px-2 py-1 ${estadoInfo(r.estado).c}`}>{estadoInfo(r.estado).l}</span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
